<script lang="ts" setup>
import { MediaImageFragment } from '~/api/graphql/generated'

export interface AuthorProps {
  image: MediaImageFragment
  name: string
  job?: string
  email?: string
  phone?: string
}

const props = defineProps<{
  title?: string
  author: AuthorProps
}>()

const { t } = useI18n()

const trimmedEmail = ref(props.author.email?.replace(/\s+/g, ''))
const trimmedPhone = ref(props.author.phone?.replace(/\s+/g, ''))

const formattedPhone = computed(() => {
  const phone = props.author.phone
  if (phone?.length !== 12) {
    return phone
  }

  return `${phone.substring(0, 3)} ${phone.substring(3, 5)} ${phone.substring(5, 8)} ${phone.substring(
    8,
    10,
  )} ${phone.substring(10)}`
})

const controller = useModal()
</script>

<template>
  <SidebarSection>
    <h2 v-if="title" v-animate="{ animation: 'vertical-reveal' }" class="title">{{ title }}</h2>
    <div v-animate="{ animation: 'vertical-reveal' }" class="author">
      <div class="image">
        <Image :entity="author.image" type="author-thumbnail" />
      </div>
      <div class="info">
        <p class="name">{{ author.name }}</p>
        <p class="text-xsmall">{{ author.job }}</p>
        <ul v-if="trimmedEmail || trimmedPhone" class="contact cul">
          <li v-if="trimmedPhone">
            <NuxtLink class="text-small" :href="`tel:${trimmedPhone}`"> {{ formattedPhone }} </NuxtLink>
          </li>
          <li v-if="trimmedEmail">
            <TextLink v-if="trimmedEmail" class="text-small" @click="controller.open">
              {{ t('author.emailLabel') }}
            </TextLink>
            <ModalContactForm v-if="trimmedEmail" :controller="controller">
              <EmailContactForm :email-to="trimmedEmail" :recipient-name="author.name" />
            </ModalContactForm>
          </li>
        </ul>
      </div>
    </div>
  </SidebarSection>
</template>

<style lang="scss" scoped>
.title {
  margin-bottom: 30px;
  @include f-21;
}

.author {
  display: grid;
  grid-template-columns: 80px auto;
  gap: 15px;

  .image {
    width: 100%;
    border-radius: 50%;

    aspect-ratio: 1/1;

    overflow: hidden;

    :deep(img) {
      object-fit: cover;
    }
  }

  .info {
    color: var(--c-text-medium);
    margin-top: 20px;
  }

  .name {
    font-weight: bold;
    color: var(--c-primary);
    margin: 0;
    @include f-18;
  }

  p {
    margin: 0;

    color: inherit;
  }

  a {
    color: inherit;
  }

  .contact {
    margin-top: 16px;
  }
}
</style>
